import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  MinusIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

export const WIZARD_STEPS = [
  { number: 1, label: 'Type' },
  { number: 2, label: 'Reference' },
  { number: 3, label: 'Select' },
  { number: 4, label: 'Build' },
];

export const INITIAL_WIZARD_DATA = {
  engagementType:   null,
  componentType:    null,
  componentName:    null,
  useReference:     null,
  selectedBaseline: null,
  filters: {
    searchClient: '',
    industry:     '',
    clientSize:   '',
    complexity:   '',
  },
  selectedTemplate: null,
  clientName:       '',
  services:         [],
  bidFactors: {
    industry:             '',
    clientSize:           'Medium',
    location:             '',
    projectComplexity:    'Medium',
    timeline:             'Standard',
    relationshipStatus:   'New',
    competitiveSituation: 'Unknown',
    paymentTerms:         'Net 30',
    scopeClarity:         'Clear',
    growthPotential:      'Medium',
    riskLevel:            'Low',
  },
};

export const BASELINE_TEMPLATES = {
  'retainer-a': [
    {
      name: 'Media Relations',
      items: [
        { role: 'Sr AM',         hours: 25, notes: 'Monthly media outreach and relationship building' },
        { role: 'Acct Mgr',      hours: 35, notes: 'Press release writing and pitch development' },
      ],
    },
    {
      name: 'Social Media',
      items: [
        { role: 'Asst Acct Mgr', hours: 40, notes: 'Content creation and community management' },
        { role: 'Mktg Support',  hours: 10, notes: 'Asset preparation' },
      ],
    },
    {
      name: 'Strategic Planning',
      items: [{ role: 'Principal', hours: 8, notes: 'Monthly strategy sessions' }],
    },
  ],
  'retainer-b': [
    {
      name: 'Media Relations',
      items: [
        { role: 'Sr AM',    hours: 20, notes: 'Targeted media outreach' },
        { role: 'Acct Mgr', hours: 30, notes: 'Content development' },
      ],
    },
    {
      name: 'Media Monitoring',
      items: [{ role: 'Asst Acct Mgr', hours: 15, notes: 'Clip tracking and reporting' }],
    },
  ],
  'retainer-c': [
    {
      name: 'Social Media Management',
      items: [
        { role: 'Asst Acct Mgr', hours: 30, notes: 'Content creation and posting' },
        { role: 'Mktg Support',  hours: 8,  notes: 'Design support' },
      ],
    },
    {
      name: 'Digital Strategy',
      items: [{ role: 'Acct Mgr', hours: 12, notes: 'Monthly planning and analytics' }],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// CANONICAL ROLE NAMES
// ─────────────────────────────────────────────────────────────────────────────

export const CANONICAL_ROLES = [
  'Principal',
  'Sr AM',
  'Acct Mgr',
  'Asst Acct Mgr',
  'Mktg Support',
  'Admin Support',
  'Freelance',
];

export const ROLE_DISPLAY_NAMES = {
  'Principal':     'Principal',
  'Sr AM':         'Sr. Account Manager',
  'Acct Mgr':      'Account Manager',
  'Asst Acct Mgr': 'Asst. Account Manager',
  'Mktg Support':  'Marketing Support',
  'Admin Support': 'Admin Support',
  'Freelance':     'Freelance',
};

export const getRoleDisplayName = (role) => ROLE_DISPLAY_NAMES[role] ?? role;

export const RANK_ALIASES = {
  'Principal':                  'Principal',
  'CEO':                        'Principal',
  'Sr AM':                      'Sr AM',
  'Sr. AM':                     'Sr AM',
  'Senior AM':                  'Sr AM',
  'Senior Account Manager':     'Sr AM',
  'Sr. Account Manager':        'Sr AM',
  'srAM':                       'Sr AM',
  'Acct Mgr':                   'Acct Mgr',
  'Account Manager':            'Acct Mgr',
  'AM':                         'Acct Mgr',
  'acctMgr':                    'Acct Mgr',
  'Asst Acct Mgr':              'Asst Acct Mgr',
  'Asst AM':                    'Asst Acct Mgr',
  'AAM':                        'Asst Acct Mgr',
  'Asst Account Mgr':           'Asst Acct Mgr',
  'Assistant Account Manager':  'Asst Acct Mgr',
  'asstAM':                     'Asst Acct Mgr',
  'Mktg Support':               'Mktg Support',
  'Marketing Support':          'Mktg Support',
  'mktgSupport':                'Mktg Support',
  'Admin Support':              'Admin Support',
  'Freelance':                  'Freelance',
  'Team Member':                'Freelance',
  'freelance':                  'Freelance',
  'Other':                      'Other',
};

export const normalizeRank = (rank) => RANK_ALIASES[rank] ?? rank;

export const BASELINE_ROLE_MAP = {
  srAM:        'Sr AM',
  acctMgr:     'Acct Mgr',
  asstAM:      'Asst Acct Mgr',
  mktgSupport: 'Mktg Support',
  admSupport:  'Admin Support',
  principal:   'Principal',
  freelance:   'Freelance',
};

// ─────────────────────────────────────────────────────────────────────────────
export const SERVICE_CATEGORIES = [
  'General',
  'Awards & Rankings',
  'Advertising',
  'Litigation (PR)',
  'Marketing Materials',
  'Media Coverage',
  'Newsletters',
  'Nominations',
  'Search Engine Optimize',
  'Social Media',
  'Speaking',
  'Strategic Partnership',
  'Webinars',
  'Website',
];

export const SERVICE_NAME_MAP = {
  // QB product_service values
  'Media':                'Media Coverage',
  'Strategic Marketing':  'Strategic Partnership',
  'Project Management':   'General',
  'General Admin':        'General',
  'Hours':                'General',
  'Business Development': 'General',
  // Baseline service names
  'SEO':                  'Search Engine Optimize',
  // Pass-throughs (already match SERVICE_CATEGORIES exactly)
  'Advertising':          'Advertising',
  'Marketing Materials':  'Marketing Materials',
  'Newsletters':          'Newsletters',
  'Nominations':          'Nominations',
  'Social Media':         'Social Media',
  'Speaking':             'Speaking',
  'Webinars':             'Webinars',
  'Website':              'Website',
};



export const DEFAULT_MARGIN = 60;
export const MARGIN_MIN     = 0;
export const MARGIN_MAX     = 100;

// ─── Preset baseline data ────────────────────────────────────────────────────
//
// Each service now carries `roleHrs` — specific hours per canonical role for
// that service.  roleHrs values must sum to annualHrs for the service.
// The top-level `staffMix` (global %) is retained only for backward-compat
// with anything that still reads it; the source of truth is now roleHrs.
//
// Role keys used: 'Principal' | 'Sr AM' | 'Acct Mgr' | 'Asst Acct Mgr'
//                 | 'Mktg Support' | 'Admin Support' | 'Freelance'
// ─────────────────────────────────────────────────────────────────────────────

export const BASELINES = [
  // ── MODEL (576 total hrs) ──────────────────────────────────────────────────
  // Global split: Sr AM 8%, Asst Acct Mgr 66%, Mktg Support 10%,
  //               Principal 1%, Freelance 15%
  {
    id:          'model',
    label:       'Model',
    tag:         'Template',
    description: 'Full-service standard template — balanced mix across all core services',
    icon:        ChartBarIcon,
    color:       'violet',
    annualFee:   47550,
    monthlyFee:  3963,
    annualHrs:   576,
    monthlyHrs:  48,
    services: {
      Advertising: {
        annualHrs: 47,
        roleHrs: { 'Asst Acct Mgr': 40, 'Mktg Support': 3, 'Freelance': 4 },
      },
      'Marketing Materials': {
        annualHrs: 17,
        roleHrs: { 'Asst Acct Mgr': 5, 'Mktg Support': 10, 'Freelance': 2 },
      },
      Media: {
        annualHrs: 86,
        roleHrs: { 'Sr AM': 0, 'Asst Acct Mgr': 55, 'Mktg Support': 5, 'Principal': 1, 'Freelance': 25 },
      },
      Newsletters: {
        annualHrs: 31,
        roleHrs: { 'Sr AM': 1, 'Asst Acct Mgr': 10, 'Mktg Support': 10, 'Principal': 0, 'Freelance': 10 },
      },
      Nominations: {
        annualHrs: 60,
        roleHrs: { 'Sr AM': 0, 'Asst Acct Mgr': 0, 'Mktg Support': 45, 'Principal': 0, 'Freelance': 15 },
      },
      SEO: {
        annualHrs: 40,
        roleHrs: { 'Sr AM': 0, 'Asst Acct Mgr': 40, 'Mktg Support': 0, 'Principal': 0, 'Freelance': 0 },
      },
      'Social Media': {
        annualHrs: 53,
        roleHrs: { 'Sr AM': 0, 'Asst Acct Mgr': 30, 'Mktg Support': 18, 'Principal': 0, 'Freelance': 5 },
      },
      Speaking: {
        annualHrs: 0,
        roleHrs: {},
      },
      'Strategic Marketing': {
        annualHrs: 147,
        roleHrs: { 'Sr AM': 45, 'Asst Acct Mgr': 90, 'Mktg Support': 5, 'Principal': 5, 'Freelance': 2 },
      },
      Webinars: {
        annualHrs: 10,
        roleHrs: { 'Sr AM': 0, 'Asst Acct Mgr': 3, 'Mktg Support': 2, 'Principal': 0, 'Freelance': 5 },
      },
      Website: {
        annualHrs: 85,
        roleHrs: { 'Sr AM': 0, 'Asst Acct Mgr': 60, 'Mktg Support': 5, 'Principal': 0, 'Freelance': 20 },
      },
    },
    guidelines: { principalUnder5: true, freelanceUnder15: false, seniorUnder70: false, strategicPct: 26 },
  },

  // ── HIGH (603.6 total hrs) ─────────────────────────────────────────────────
  // Global split: Sr AM 1%, Asst Acct Mgr 70%, Mktg Support 8%,
  //               Principal 5%, Freelance 17%
  {
    id:          'high',
    label:       'High',
    tag:         'Larger Client',
    description: 'Larger client profile — heavy strategic marketing, nominations & website focus',
    icon:        ArrowTrendingUpIcon,
    color:       'emerald',
    annualFee:   49950,
    monthlyFee:  4163,
    annualHrs:   603.6,
    monthlyHrs:  50.3,
    services: {
      Advertising: {
        annualHrs: 51.7,
        roleHrs: { 'Asst Acct Mgr': 32, 'Mktg Support': 10, 'Freelance': 9.7 },
      },
      'Marketing Materials': {
        annualHrs: 13.4,
        roleHrs: { 'Asst Acct Mgr': 8, 'Mktg Support': 5.4 },
      },
      Media: {
        annualHrs: 72.8,
        roleHrs: { 'Sr AM': 6, 'Asst Acct Mgr': 52, 'Freelance': 14.8 },
      },
      Newsletters: {
        annualHrs: 19.5,
        roleHrs: { 'Asst Acct Mgr': 14, 'Mktg Support': 5.5 },
      },
      Nominations: {
        annualHrs: 73.2,
        roleHrs: { 'Asst Acct Mgr': 55, 'Mktg Support': 8, 'Freelance': 10.2 },
      },
      SEO: {
        annualHrs: 42.1,
        roleHrs: { 'Asst Acct Mgr': 25, 'Mktg Support': 7, 'Freelance': 10.1 },
      },
      'Social Media': {
        annualHrs: 59.3,
        roleHrs: { 'Asst Acct Mgr': 44, 'Mktg Support': 8, 'Freelance': 7.3 },
      },
      Speaking: {
        annualHrs: 0,
        roleHrs: {},
      },
      'Strategic Marketing': {
        annualHrs: 177.5,
        roleHrs: { 'Principal': 30, 'Asst Acct Mgr': 110, 'Freelance': 37.5 },
      },
      Webinars: {
        annualHrs: 0,
        roleHrs: {},
      },
      Website: {
        annualHrs: 94.1,
        roleHrs: { 'Asst Acct Mgr': 83, 'Mktg Support': 4.1, 'Freelance': 7 },
      },
    },
    guidelines: { principalUnder5: true, freelanceUnder15: false, seniorUnder70: false, strategicPct: 29 },
  },

  // ── MEDIUM (547.4 total hrs) ───────────────────────────────────────────────
  // Global split: Sr AM 18%, Asst Acct Mgr 52%, Mktg Support 10%,
  //               Principal 0%, Freelance 20%
  {
    id:          'medium',
    label:       'Medium',
    tag:         'Mid-Size Client',
    description: 'Mid-size client — strong media & strategic marketing with newsletter programs',
    icon:        MinusIcon,
    color:       'blue',
    annualFee:   49648,
    monthlyFee:  4137,
    annualHrs:   547.4,
    monthlyHrs:  45.6,
    services: {
      Advertising: {
        annualHrs: 0,
        roleHrs: {},
      },
      'Marketing Materials': {
        annualHrs: 18.7,
        roleHrs: { 'Asst Acct Mgr': 12, 'Mktg Support': 6.7 },
      },
      Media: {
        annualHrs: 150.8,
        roleHrs: { 'Sr AM': 50, 'Asst Acct Mgr': 70, 'Freelance': 30.8 },
      },
      Newsletters: {
        annualHrs: 69.7,
        roleHrs: { 'Sr AM': 20, 'Asst Acct Mgr': 35, 'Mktg Support': 10, 'Freelance': 4.7 },
      },
      Nominations: {
        annualHrs: 13.6,
        roleHrs: { 'Asst Acct Mgr': 8, 'Mktg Support': 5.6 },
      },
      SEO: {
        annualHrs: 5.8,
        roleHrs: { 'Asst Acct Mgr': 4, 'Freelance': 1.8 },
      },
      'Social Media': {
        annualHrs: 51.2,
        roleHrs: { 'Sr AM': 8, 'Asst Acct Mgr': 28, 'Mktg Support': 10, 'Freelance': 5.2 },
      },
      Speaking: {
        annualHrs: 0.4,
        roleHrs: { 'Sr AM': 0.4 },
      },
      'Strategic Marketing': {
        annualHrs: 150.8,
        roleHrs: { 'Sr AM': 18, 'Asst Acct Mgr': 95, 'Mktg Support': 10, 'Freelance': 27.8 },
      },
      Webinars: {
        annualHrs: 11.3,
        roleHrs: { 'Asst Acct Mgr': 8, 'Mktg Support': 3.3 },
      },
      Website: {
        annualHrs: 75.1,
        roleHrs: { 'Sr AM': 2.6, 'Asst Acct Mgr': 27.6, 'Mktg Support': 9.1, 'Freelance': 35.8 },
      },
    },
    guidelines: { principalUnder5: true, freelanceUnder15: false, seniorUnder70: true, strategicPct: 28 },
  },

  // ── LOW (281.8 total hrs) ──────────────────────────────────────────────────
  // Global split: Sr AM 19%, Acct Mgr 42%, Mktg Support 6%,
  //               Principal 2%, Freelance 31%
  {
    id:          'low',
    label:       'Low',
    tag:         'Small Client',
    description: 'Smaller client — media-heavy engagement with focused strategic support',
    icon:        ArrowTrendingDownIcon,
    color:       'amber',
    annualFee:   36135,
    monthlyFee:  3011,
    annualHrs:   281.8,
    monthlyHrs:  23.5,
    services: {
      Advertising: {
        annualHrs: 0,
        roleHrs: {},
      },
      'Marketing Materials': {
        annualHrs: 0,
        roleHrs: {},
      },
      Media: {
        annualHrs: 138,
        roleHrs: { 'Sr AM': 30, 'Acct Mgr': 60, 'Freelance': 48 },
      },
      Newsletters: {
        annualHrs: 0,
        roleHrs: {},
      },
      Nominations: {
        annualHrs: 23,
        roleHrs: { 'Acct Mgr': 15, 'Mktg Support': 5, 'Freelance': 3 },
      },
      SEO: {
        annualHrs: 0,
        roleHrs: {},
      },
      'Social Media': {
        annualHrs: 16.5,
        roleHrs: { 'Acct Mgr': 10, 'Mktg Support': 4, 'Freelance': 2.5 },
      },
      Speaking: {
        annualHrs: 7.1,
        roleHrs: { 'Sr AM': 5, 'Acct Mgr': 2.1 },
      },
      'Strategic Marketing': {
        annualHrs: 55.5,
        roleHrs: { 'Principal': 5.6, 'Sr AM': 18.5, 'Acct Mgr': 14, 'Freelance': 17.4 },
      },
      Webinars: {
        annualHrs: 0,
        roleHrs: {},
      },
      Website: {
        annualHrs: 41.7,
        roleHrs: { 'Acct Mgr': 17.3, 'Mktg Support': 7.9, 'Freelance': 16.5 },
      },
    },
    guidelines: { principalUnder5: true, freelanceUnder15: false, seniorUnder70: false, strategicPct: 20 },
  },
];